import {
  AccountInfo,
  AuthenticationResult,
  EventPayload,
  IPublicClientApplication,
} from '@azure/msal-browser'
import axios, { AxiosResponse } from 'axios'
import { b2cPolicies } from '../authConfig'

let redirectInProgress = false

const acquireAccessToken = async (
  msalInstance: IPublicClientApplication,
  scopes: string[],
): Promise<string> => {
  try {
    const activeAccount = msalInstance.getActiveAccount()
    const accounts = msalInstance.getAllAccounts()

    if (!activeAccount && accounts.length === 0) {
      if (!redirectInProgress) {
        redirectInProgress = true
        console.log('No active account found, redirecting to login')
        const loginRequest = {
          authority: b2cPolicies.authorities.signUpSignIn.authority,
          scopes,
        }
        await msalInstance.loginRedirect(loginRequest)
      }
      throw new Error('Login redirect initiated')
    }

    const account = activeAccount || accounts[0]
    const request = {
      scopes,
      account,
    }

    try {
      const authResult = await msalInstance.acquireTokenSilent(request)
      return authResult.accessToken
    } catch (error) {
      console.log('Silent token acquisition failed, redirecting:', error)
      if (!redirectInProgress) {
        redirectInProgress = true
        await msalInstance.acquireTokenRedirect({ scopes })
      }
      throw new Error('Redirect initiated')
    }
  } catch (error) {
    console.error('Error acquiring token:', error)
    throw error
  }
}

export const authorizedGet = async <T>(
  msalInstance: IPublicClientApplication,
  endpoint: string,
  scopes: string[],
): Promise<T> => {
  const accessToken = await acquireAccessToken(msalInstance, scopes)
  const response = await axios.get(endpoint, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  })
  return response.data
}

export const authorizedPost = async <T>(
  instance: IPublicClientApplication,
  url: string,
  data: unknown,
  scopes: string[],
): Promise<T> => {
  const accessToken = await acquireAccessToken(instance, scopes)
  const response: AxiosResponse<T> = await axios.post(url, data, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
  })
  return response.data
}

export const authorizedDelete = async <T>(
  instance: IPublicClientApplication,
  url: string,
  scopes: string[],
): Promise<T> => {
  const accessToken = await acquireAccessToken(instance, scopes)
  const response: AxiosResponse<T> = await axios.delete(url, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
  })
  return response.data
}

export const authorizedPatch = async <T>(
  instance: IPublicClientApplication,
  url: string,
  data: unknown,
  scopes: string[],
): Promise<T> => {
  const accessToken = await acquireAccessToken(instance, scopes)
  const response: AxiosResponse<T> = await axios.patch(url, data, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
  })
  return response.data
}

export const authorizedPut = async <T>(
  instance: IPublicClientApplication,
  url: string,
  data: unknown,
  scopes: string[],
): Promise<T> => {
  const accessToken = await acquireAccessToken(instance, scopes)
  const response: AxiosResponse<T> = await axios.put(url, data, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
  })
  return response.data
}

export function compareIssuingPolicy(idTokenClaims: any, policyToCompare: any) {
  let tfpMatches =
    idTokenClaims.hasOwnProperty('tfp') &&
    idTokenClaims['tfp'].toLowerCase() === policyToCompare.toLowerCase()
  let acrMatches =
    idTokenClaims.hasOwnProperty('acr') &&
    idTokenClaims['acr'].toLowerCase() === policyToCompare.toLowerCase()
  return tfpMatches || acrMatches
}

export function isAccountInfo(obj: any): obj is AccountInfo {
  return obj && 'idTokenClaims' in obj
}

export function isAuthenticationResult(
  payload: EventPayload,
): payload is AuthenticationResult {
  return !!payload && !!(payload as AuthenticationResult).account
}

const ADMIN_USER_IDS = new Set([
  'd090450a-f339-4d6e-aa90-b0f4aac0e9f6',
  '0f897e5d-6b98-4b88-af88-ef50216108a2',
  '8396a51e-49c8-4e32-a29a-f01094355497',
])

export const isUserAdmin = (userId: string | undefined): boolean => {
  if (!userId) return false
  return ADMIN_USER_IDS.has(userId)
}
